import _ from 'lodash';
import app from '$$app';
import moment from 'moment';
import constants from '$$helpers/constants';
import timeTrackingModel from '$$timeTrackingModel';
import projectsModel from '$$projectModel';
import Store from '$$globalStore';
import customDayTemplatesModel from '$$customDayTemplatesModel';
import chartsConfig from './chartsConfig';
import reportHelpers from '../include/helpers';
import globalToast from '$$vueCmp/toast';

const MONTH_IDS = ['jan', 'feb', 'march', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

const MONTH_LOCALES = [
  'report_month_jan',
  'report_month_feb',
  'report_month_march',
  'report_month_apr',
  'report_month_may',
  'report_month_jun',
  'report_month_jul',
  'report_month_aug',
  'report_month_sep',
  'report_month_oct',
  'report_month_nov',
  'report_month_dec',
];

class TimeReport {
  constructor() {
    this.timeWorker = null;
    this.reportTasks = {};
    this.calculationMode = null;
    this.totalRange = {
      start: null,
      end: null,
    };
    this.baselineTotalRange = {
      start: null,
      end: null,
    };
  }

  initTimeWorker() {
    if (window.Worker && !this.timeWorker) {
      let version = Date.now();

      if (GT.productVersion && GT.productVersion.buildVersion) {
        version = GT.productVersion.buildVersion;
      }

      this.timeWorker = new Worker('/workers/reports_time_worker.js' + `?${version}`);
    }

    this._initEvents();
  }

  _initEvents() {
    const that = this;

    this.timeWorker.onmessage = function (e) {
      that.calculateMonthTimeLogReport(e.data);
    };

    this.timeWorker.onerror = function (e) {
      globalToast.error(__('time_calculating_error'));
    };
  }

  calculateTime(tasks, baselineTasks, calculationMode) {
    this.calculationMode = calculationMode;
    this.reportTasks = {};

    const currentFilter = Store.getters['filter/currentFilter'];
    const defaultRange = reportHelpers.defaultRange().last_6_months;

    const totalEstimate = tasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);
    const baselineTotalEstimate = baselineTasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);

    let tasksAndMilestones = tasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);
    let baselineTasksAndMilestones = baselineTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);

    tasksAndMilestones = reportHelpers.prepareTaskResourcesForWorker(tasksAndMilestones);
    baselineTasksAndMilestones = reportHelpers.prepareTaskResourcesForWorker(baselineTasksAndMilestones);

    const range = {
      start: currentFilter?.properties?.rangeDate?.start || totalEstimate?.start_date || defaultRange.start,
      end: currentFilter?.properties?.rangeDate?.end || totalEstimate?.end_date || defaultRange.end,
    };

    const baselineRange = {
      start: currentFilter?.properties?.rangeDate?.start || baselineTotalEstimate?.start_date || totalEstimate?.start_date || defaultRange.start,
      end: currentFilter?.properties?.rangeDate?.end || baselineTotalEstimate?.end_date || totalEstimate?.end_date || defaultRange.end,
    };

    this.totalRange = { ...range };
    this.baselineTotalRange = { ...baselineRange };

    const filteredTasks = this.returnTasksByCurrentFilter(tasksAndMilestones, currentFilter);
    const filteredBaselineTasks = this.returnTasksByCurrentFilter(baselineTasksAndMilestones, currentFilter);
    const tasksByMonth = this.prepareMonthTasks(filteredTasks, filteredBaselineTasks, range);

    const preparedWorkerTasks = {
      allFilteredTasks: { tasks: filteredTasks, range },
      allFilteredBaselineTasks: { tasks: filteredBaselineTasks, range: baselineRange },
      ...tasksByMonth,
    };

    Object.entries(preparedWorkerTasks).forEach(([key, data]) => {
      this.reportTasks[key] = data.tasks;
    });
    this.sendDataToWorker(preparedWorkerTasks);
  }

  prepareMonthTasks(tasks, baselineTasks, range) {
    const startDate = moment(range.start);
    const endDate = moment(range.end);
    const startMonth = startDate.clone().startOf('month');
    const endMonth = endDate.clone().startOf('month');
    const monthsDifference = endMonth.diff(startMonth, 'months');
    const preparedTasksMap = {};

    for (let i = 0; i <= monthsDifference; i++) {
      const currentMonth = moment(range.start).add(i, 'months');
      let firstDayOfCurrentMonth = currentMonth.clone().startOf('month');
      let lastDayOfCurrentMonth = currentMonth.clone().endOf('month');

      if (i === 0) {
        firstDayOfCurrentMonth = moment(range.start);
      }

      if (i === monthsDifference) {
        lastDayOfCurrentMonth = moment(range.end);
      }

      const tasksByMonth = tasks.filter(task => reportHelpers.returnTaskByRange(task, { start: firstDayOfCurrentMonth, end: lastDayOfCurrentMonth }));
      const baselineTasksByMonth = baselineTasks.filter(task => reportHelpers.returnTaskByRange(task, { start: firstDayOfCurrentMonth, end: lastDayOfCurrentMonth }));

      const id = `${MONTH_IDS[currentMonth.month()]}-${currentMonth.year()}`;

      const workerRange = {
        start: firstDayOfCurrentMonth.valueOf(),
        end: lastDayOfCurrentMonth.valueOf(),
      };

      preparedTasksMap[id] = { tasks: tasksByMonth, range: workerRange };
      preparedTasksMap[`${id}-baseline`] = { tasks: baselineTasksByMonth, range: workerRange };
    }

    return preparedTasksMap;
  }

  sendDataToWorker(tasksData) {
    const projectSettingsMap = {};

    projectsModel.serialize().forEach(settings => {
      projectSettingsMap[settings.gantt_id] = {
        ...settings,
        totalEstimate: Store.getters['tasksModel/getTotalEstimateDataForProject'](settings.gantt_id),
      };
    });

    this.timeWorker.postMessage(_.cloneDeep({
      projectConfigs: reportHelpers.prepareProjectConfigs(),
      projectSettings: projectSettingsMap,
      customDaysData: customDayTemplatesModel.serialize(),
      tasksData,
    }));
  }

  returnTasksByCurrentFilter(tasks, currentFilter) {
    const startDate = currentFilter?.properties?.rangeDate?.start ? moment(currentFilter.properties.rangeDate.start) : null;
    const endDate = currentFilter?.properties?.rangeDate?.end ? moment(currentFilter.properties.rangeDate.end) : null;
    const filterRange = startDate && endDate ? { start: startDate, end: endDate } : null;

    return tasks.filter(task => reportHelpers.returnTaskByRange(task, filterRange)).filter(task => {
      if (!currentFilter?.properties?.assignee?.length) {
        return task;
      }

      if (currentFilter.properties.assignee.includes('-1') && !task.resources.length) {
        return task;
      }

      const assigneeTask = task.resources.find(res => currentFilter.properties.assignee.includes(+res.resource_id));

      if (assigneeTask) {
        return task;
      }
    }).filter(task => {
      if (!currentFilter?.properties?.text?.length) {
        return task;
      }

      return task.text.toLowerCase().startsWith(currentFilter.properties.text.toLowerCase());
    });
  }

  calculateMonthTimeLogReport(data) {
    let cumulativeEstimationResult = 0;
    let baselineCumulativeEstimationResult = 0;
    let cumulativeLogsResult = 0;

    const reportTasks = data || this.reportTasks;

    if (!reportTasks.allFilteredTasks?.length) {
      app.trigger('onAfterMonthTimeReportCalculation', null);

      return;
    }

    const xAxisValues = [];

    const startMonth = moment(this.totalRange.start).clone().startOf('month');
    const endMonth = moment(this.totalRange.end).clone().startOf('month');
    const monthsDifference = endMonth.diff(startMonth, 'months');
    const resultConfig = _.cloneDeep(chartsConfig.monthReport.default);

    const isBaseline = this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE;

    for (let i = 0; i <= monthsDifference; i++) {
      const currentMonth = moment(this.totalRange.start).add(i, 'months');
      const startOfMonth = currentMonth.clone().startOf('month');
      const endOfMonth = currentMonth.clone().endOf('month');
      const dateString = `${MONTH_LOCALES[currentMonth.month()]} ${currentMonth.year()}`;
      const dateKey = `${MONTH_IDS[currentMonth.month()]}-${currentMonth.year()}`;
      const baselineDateKey = `${dateKey}-baseline`;

      xAxisValues.push(dateString);

      let tasksByMonth = isBaseline ? reportTasks[baselineDateKey] : reportTasks[dateKey];

      if (this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
        tasksByMonth = reportTasks[dateKey];
        const baselineTasksByMonth = reportTasks[baselineDateKey];

        const currentMonthEstimation = tasksByMonth.reduce((sum, task) => sum + task.estimation, 0);
        const baselineCurrentMonthEstimation = baselineTasksByMonth.reduce((sum, task) => sum + task.estimation, 0);

        cumulativeEstimationResult += currentMonthEstimation;
        baselineCumulativeEstimationResult += baselineCurrentMonthEstimation;

        resultConfig.series = resultConfig.series.filter(item => !['logs', 'cumulativeLogs'].includes(item.name)).map(item => {
          if (item.name === 'estimation') {
            return {
              ...item,
              data: [
                ...item.data,
                {
                  value: currentMonthEstimation,
                  label: {
                    formatter: `${currentMonthEstimation}`,
                    position: 'insideTop',
                  },
                  customData: {
                    label: item.locale,
                    value: currentMonthEstimation && typeof currentMonthEstimation === 'number' ? currentMonthEstimation.toFixed(2) : +currentMonthEstimation || 0,
                  },
                },
              ],
            };
          }

          if (item.name === 'baselineEstimation') {
            return {
              ...item,
              data: [
                ...item.data,
                {
                  value: baselineCurrentMonthEstimation,
                  label: {
                    position: 'insideTop',
                  },
                  customData: {
                    label: item.locale,
                    value: baselineCurrentMonthEstimation && typeof baselineCurrentMonthEstimation === 'number' ? baselineCurrentMonthEstimation.toFixed(2) : +baselineCurrentMonthEstimation || 0,
                  },
                },
              ],
            };
          }

          if (item.name === 'cumulativeEstimation') {
            return {
              ...item,
              data: [
                ...item.data,
                {
                  value: cumulativeEstimationResult,
                  customData: {
                    label: item.locale,
                    value: cumulativeEstimationResult && cumulativeEstimationResult === 'number' ? cumulativeEstimationResult.toFixed(2) : +cumulativeEstimationResult || 0,
                  },
                },
              ],
            };
          }

          if (item.name === 'cumulativeBaselineEstimation') {
            return {
              ...item,
              data: [
                ...item.data,
                {
                  value: baselineCumulativeEstimationResult,
                  customData: {
                    label: item.locale,
                    value: baselineCumulativeEstimationResult && typeof baselineCumulativeEstimationResult === 'number' ? baselineCumulativeEstimationResult.toFixed(2) : +baselineCumulativeEstimationResult || 0,
                  },
                },
              ],
            };
          }

          return item;
        });
      } else {
        let currentMonthLogsValue = 0;

        tasksByMonth.forEach(task => {
          const logsData = timeTrackingModel.getLogsByParams(task.gantt_id, task.id);

          if (logsData?.logs) {
            const sum = logsData.logs.filter(log => {
              const logDate = moment(log.date);

              if (logDate.isBefore(startOfMonth)) {
                return false;
              }

              if (logDate.isAfter(endOfMonth)) {
                return false;
              }

              return true;
            }).reduce((sum, log) => sum + log.time, 0);

            currentMonthLogsValue += isNaN(sum) ? 0 : +(sum / 60).toFixed(2);
            // const sum = logsData.logs.reduce((sum, log) => sum + log.time, 0);

            // currentMonthLogsValue = +(sum / 60).toFixed(2) || 0;
          }
        });

        const currentMonthEstimation = tasksByMonth.reduce((sum, task) => sum + task.estimation, 0);

        cumulativeEstimationResult += currentMonthEstimation;
        cumulativeLogsResult += currentMonthLogsValue;

        let seriesItems = resultConfig.series.filter(item => !['baselineEstimation', 'cumulativeBaselineEstimation'].includes(item.name));

        if (isBaseline) {
          seriesItems = resultConfig.series.filter(item => !['estimation', 'cumulativeEstimation'].includes(item.name));
        }

        resultConfig.series = seriesItems.map(item => {
          if (item.name === 'estimation' || item.name === 'baselineEstimation') {
            const changeColor = {};

            if (isBaseline && item.name === 'baselineEstimation') {
              changeColor.color = '#8BC34A';
            }

            return {
              ...item,
              ...changeColor,
              data: [
                ...item.data,
                {
                  value: currentMonthEstimation,
                  label: {
                    formatter: `${currentMonthEstimation}`,
                    position: 'insideTop',
                  },
                  customData: {
                    label: item.locale,
                    value: currentMonthEstimation && typeof currentMonthEstimation === 'number' ? currentMonthEstimation.toFixed(2) : +currentMonthEstimation || 0,
                  },
                },
              ],
            };
          }

          if (item.name === 'logs') {
            return {
              ...item,
              data: [
                ...item.data,
                {
                  value: currentMonthLogsValue,
                  label: {
                    formatter: `${currentMonthLogsValue}`,
                    position: 'insideTop',
                  },
                  customData: {
                    label: item.locale,
                    value: currentMonthLogsValue && typeof currentMonthLogsValue === 'number' ? currentMonthLogsValue.toFixed(2) : +currentMonthLogsValue || 0,
                  },
                },
              ],
            };
          }

          if (item.name === 'cumulativeEstimation' || item.name === 'cumulativeBaselineEstimation') {
            const changeColor = {};

            if (isBaseline && item.name === 'cumulativeBaselineEstimation') {
              changeColor.color = '#6E8F3F';
            }

            return {
              ...item,
              ...changeColor,
              data: [
                ...item.data,
                {
                  value: cumulativeEstimationResult,
                  customData: {
                    label: item.locale,
                    value: cumulativeEstimationResult && typeof cumulativeEstimationResult === 'number' ? cumulativeEstimationResult.toFixed(2) : +cumulativeEstimationResult || 0,
                  },
                },
              ],
            };
          }

          if (item.name === 'cumulativeLogs') {
            return {
              ...item,
              data: [
                ...item.data,
                {
                  value: cumulativeLogsResult,
                  customData: {
                    label: item.locale,
                    value: cumulativeLogsResult && typeof cumulativeLogsResult === 'number' ? cumulativeLogsResult.toFixed(2) : +cumulativeLogsResult || 0,
                  },
                },
              ],
            };
          }

          return item;
        });
      }
    }

    const barValues = resultConfig.series.filter(item => item.type === 'bar').flatMap(item => item.data.map(obj => obj.value));
    const lineValues = resultConfig.series.filter(item => item.type === 'line').flatMap(item => item.data.map(obj => obj.value));
    const intervals = reportHelpers.calculateYAxisIntervals(barValues, lineValues);

    if (intervals.left > 0 && intervals.right > 0) {
      resultConfig.xAxis[0].data = xAxisValues;
      resultConfig.yAxis[0].interval = intervals.left;
      resultConfig.yAxis[1].interval = intervals.right;

      app.trigger('onAfterMonthTimeReportCalculation', resultConfig);

      return;
    }

    app.trigger('onAfterMonthTimeReportCalculation', null);
  }
}

const timeReport = new TimeReport();

export default timeReport;
